import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import * as m from '../../localization/out/messages.js';

/**
 * The warning above the ticket lists: tickets can be reassigned from one field engineer to another until 19:00,
 * and the Service Desk's evening email says when the assignment is finished. Dismissible, and back the next day —
 * it is true every day, so it should not stay hidden for good once closed.
 */
const STORAGE_KEY = 'smartteams-reassignment-notice-dismissed-on';

/** Local date as `yyyy-mm-dd`: "today" for the person looking at the screen. */
function today(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function dismissedToday(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === today();
  } catch {
    return false; // private-mode storage can throw; show the notice
  }
}

export const ReassignmentNotice = stateful(() => {
  const [dismissed, setDismissed] = useState(dismissedToday());
  if (dismissed) return nothing;

  const dismiss = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, today());
    } catch {
      // not persisted — it still closes for this visit
    }
    setDismissed(true);
  };

  return html`
    <div class="alert alert-warning items-start py-2 text-sm" role="alert">
      <span class="icon-[material-symbols--warning-outline] text-lg shrink-0"></span>
      <div class="flex flex-col gap-0.5 min-w-0">
        <span class="font-semibold">${m.ticket_reassign_notice_title()}</span>
        <span>${m.ticket_reassign_notice_body()}</span>
      </div>
      <button
        type="button"
        class="btn btn-ghost btn-xs btn-circle shrink-0"
        aria-label=${m.ticket_reassign_notice_dismiss()}
        @click=${dismiss}
      >
        <span class="icon-[material-symbols--close] text-sm"></span>
      </button>
    </div>
  `;
});
